import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { useSdk } from '~/sdk';
import type {
  GetCartItemsResponse,
  UpdateCartItemRequest,
  AddOrRemoveCouponRequest,
} from '@vue-storefront/prestashop-api';
import useAnalytics from '../useAnalytics';
import type { Reactive } from 'vue';
import useNotifications from '~/stores/notifications';

export const useCart = () => {
  const { addOrRemoveFromCart } = useAnalytics();
  const auth = useAuth();
  const isAuthenticated = computed(() => auth.isAuthenticated);
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const errors = ref<string[]>([]);

  // Cart query
  const cartQuery = useQuery({
    queryKey: ['cart'],
    queryFn: async () => {
      return await useSdk().commerce.getCartItems() ?? null;
    },
    enabled: computed(() => isAuthenticated.value),
  });

  // Add/update cart item mutation
  const updateCartItemMutation = useMutation({
    mutationFn: async (product: UpdateCartItemRequest) => {
      return await useSdk().commerce.updateCartItem({
        id_product: product.id_product,
        id_product_attribute: product.id_product_attribute,
        qty: product.qty,
        op: product.op || 'up',
      });
    },
    onSuccess: (_, variables) => {
      // Track analytics if not decreasing quantity
      if (variables.op !== 'down') {
        addOrRemoveFromCart(variables, 'add_to_cart');
      }

      // Invalidate cart query to refetch
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
  });

  // Remove cart item mutation
  const removeCartItemMutation = useMutation({
    mutationFn: async (product: UpdateCartItemRequest) => {
      return await useSdk().commerce.removeCartItem({
        id_product: product.id_product,
        id_product_attribute: product.id_product_attribute,
      });
    },
    onSuccess: (_, variables) => {
      // Track analytics
      addOrRemoveFromCart(variables, 'remove_from_cart');

      // Invalidate cart query to refetch
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
  });

  // Apply coupon mutation
  const applyCouponMutation = useMutation({
    mutationFn: async (params: AddOrRemoveCouponRequest) => {
      return await useSdk().commerce.addOrRemoveCoupon({
        addDiscount: params.addDiscount,
        discount_name: params.discount_name,
      });
    },
    onSuccess: (data) => {
      errors.value = [];
      errors.value.push(data.errors.length ? t(data.errors[0]) : t('Coupon successfully added'));
      // Invalidate cart query to refetch
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
  });

  // Remove coupon mutation
  const removeCouponMutation = useMutation({
    mutationFn: async (params: AddOrRemoveCouponRequest) => {
      return await useSdk().commerce.addOrRemoveCoupon({
        deleteDiscount: params.deleteDiscount,
      });
    },
    onSuccess: () => {
      // Invalidate cart query to refetch
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
  });

  // Trigger cart refetch when auth state changes
  watch(isAuthenticated, () => {
    if (isAuthenticated.value) {
      cartQuery.refetch();
    }
  });

  return {
    query: reactive(cartQuery) satisfies Reactive<typeof cartQuery>,
    mutationUpdate: reactive(updateCartItemMutation) satisfies Reactive<typeof updateCartItemMutation>,
    mutationRemove: reactive(removeCartItemMutation) satisfies Reactive<typeof removeCartItemMutation>,
    mutationApplyCoupon: reactive(applyCouponMutation) satisfies Reactive<typeof applyCouponMutation>,
    mutationRemoveCoupon: reactive(removeCouponMutation) satisfies Reactive<typeof removeCouponMutation>,

    data: computed<GetCartItemsResponse['psdata']>(() => cartQuery?.data?.value || null),
    isLoading: computed(() => cartQuery.isLoading.value),
    isFetching: computed(() => cartQuery.isFetching.value),
    isError: computed(() => cartQuery.isError.value),
    error: computed(() => cartQuery.error.value),

    errors: computed(() => errors.value),
    // Methods
    execute: () => cartQuery.refetch(),
    refresh: () => cartQuery.refetch(),

    // Cart operations
    addToCart: (product: UpdateCartItemRequest) => updateCartItemMutation.mutate(product),
    removeFromCart: (product: UpdateCartItemRequest) => removeCartItemMutation.mutate(product),
    applyCoupon: (params: AddOrRemoveCouponRequest) => applyCouponMutation.mutate(params),
    removeCoupon: (params: AddOrRemoveCouponRequest) => removeCouponMutation.mutate(params),

    // Mutation states
    isUpdating: computed(() =>
      updateCartItemMutation.isPending.value ||
      removeCartItemMutation.isPending.value ||
      applyCouponMutation.isPending.value ||
      removeCouponMutation.isPending.value
    ),
  };
};
